import React, { useState } from 'react';
import { QrCode, CreditCard, X } from 'lucide-react';
import CheckoutForm from '../checkout-form';
import CheckoutSummary from '../checkout-summary';
import { PlanType } from '../../../services/tipos';

interface StepPaymentProps {
  paymentMethod: 'pix' | 'creditcard';
  setPaymentMethod: (method: 'pix' | 'creditcard') => void;
  pixIndisponivel: boolean;
  vehicleCount: number;
  setVehicleCount: (count: number) => void;
  minVehicleLimit: number;
  selectedPlan: PlanType;
  setSelectedPlan: (plan: PlanType) => void;
  pixData?: { qrCode: string; qrCodeImage: string; paymentUrl: string; pixKey: string } | null;
  isSubmitting: boolean;
  isButtonsDisabled: boolean;
  hasActivePayment: boolean;
  hasPendingInvoice: boolean;
  formErrors: Record<string, string>;
  incluirLocaSmartCheck: boolean;
  setIncluirLocaSmartCheck: (val: boolean) => void;
  isLocaSmartCheckAtivo: boolean;
  precoLocaSmartCheckMensal: number;
  planDiscount: number;
  currentVehicleLimit: number;
  basePrice: number;
  totalPrice: number;
  discount: number;
  finalPrice: number;
  couponCode: string;
  setCouponCode: (code: string) => void;
  onCouponApply: (code: string) => void;
  countdown: number;
  temPlanoAtivo: boolean;
  diasRestantes: number;
  precoAtual: number;
  precoNovo: number;
  precoMensalSemDesconto: number;
  tipoPlano: 'PRO' | 'AVANCADO';
  acao: 'upgrade' | 'expandir' | null;
  currentPlanType?: string;
  precoLocaSmartCheckCiclo: number;
  installmentCount: number;
  setInstallmentCount: (count: number) => void;
  installmentOptions: { count: number; label: string; value: number }[];
  showInstallments: boolean;
  descontoOriginal?: number;
  descontoAjustado?: number;
  indicadorNome?: string;
  onCheckout: () => void;
}

const StepPayment: React.FC<StepPaymentProps> = (props) => {
  const {
    paymentMethod, setPaymentMethod, pixIndisponivel,
    isSubmitting, isButtonsDisabled, hasActivePayment, hasPendingInvoice,
    installmentCount, setInstallmentCount, installmentOptions, showInstallments,
    descontoOriginal, descontoAjustado,
    onCheckout,
  } = props;

  const [modalAberto, setModalAberto] = useState(false);
  const acoesBloqueadas = isSubmitting || isButtonsDisabled || hasActivePayment || hasPendingInvoice;
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fecharModal = () => { if (!isSubmitting) setModalAberto(false); };

  return (
    <div>
      <div className="grid grid-cols-[1fr_400px] gap-6 items-start">
        <div className="space-y-4">
          {/* Forma de pagamento */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <span className="block text-sm font-semibold text-[#475569] mb-2">Forma de pagamento</span>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => !pixIndisponivel && setPaymentMethod('pix')}
                disabled={acoesBloqueadas || pixIndisponivel}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-base transition-all ${
                  pixIndisponivel
                    ? 'opacity-40 border-gray-200 text-gray-400 cursor-not-allowed'
                    : paymentMethod === 'pix'
                    ? 'border-[#1F467E] bg-blue-50 text-[#1F467E]'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                <QrCode size={20} /> PIX
              </button>
              <button
                onClick={() => setPaymentMethod('creditcard')}
                disabled={acoesBloqueadas}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-bold text-base transition-all ${
                  paymentMethod === 'creditcard'
                    ? 'border-[#1F467E] bg-blue-50 text-[#1F467E]'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                <CreditCard size={20} /> Cartão
              </button>
            </div>
            {pixIndisponivel && (
              <p className="text-xs text-gray-400 mt-2">PIX não disponível para assinaturas mensais recorrentes.</p>
            )}
          </div>

          {/* Parcelamento (cartão) */}
          {showInstallments && paymentMethod === 'creditcard' && (
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 h-fit self-start">
              <label className="block text-sm font-semibold text-[#475569] mb-2">Parcelamento</label>
              <select
                value={installmentCount}
                onChange={(e) => setInstallmentCount(Number(e.target.value))}
                disabled={acoesBloqueadas}
                className="w-full px-3 py-2 rounded-lg border border-[#cbd5e1] text-sm text-[#334155] bg-white focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
              >
                {installmentOptions.map((opt) => (
                  <option key={opt.count} value={opt.count}>{opt.label}</option>
                ))}
              </select>
              {installmentCount > 1 && descontoOriginal != null && descontoAjustado != null && descontoOriginal !== descontoAjustado && (
                <p className="text-xs text-amber-600 mt-1.5 leading-snug">
                  Parcelando, o desconto do ciclo cai para {(descontoAjustado * 100).toFixed(0)}% (era {(descontoOriginal * 100).toFixed(0)}%).
                </p>
              )}
            </div>
          )}

          {/* Configuração do plano (veículos, ciclo, addon) — sempre visível */}
          <CheckoutForm
            vehicleCount={props.vehicleCount}
            setVehicleCount={props.setVehicleCount}
            paymentMethod={paymentMethod}
            selectedPlan={props.selectedPlan}
            setSelectedPlan={props.setSelectedPlan}
            pixData={undefined}
            isSubmitting={isSubmitting || hasActivePayment}
            onCheckout={onCheckout}
            formErrors={props.formErrors}
            hasActivePayment={hasActivePayment}
            hasPendingInvoice={hasPendingInvoice}
            isExpansion={false}
            isUpgrade={false}
            minVehicleLimit={props.minVehicleLimit}
            incluirLocaSmartCheck={props.incluirLocaSmartCheck}
            setIncluirLocaSmartCheck={props.setIncluirLocaSmartCheck}
            isLocaSmartCheckAtivo={props.isLocaSmartCheckAtivo}
            precoLocaSmartCheckMensal={props.precoLocaSmartCheckMensal}
            planDiscount={props.planDiscount}
            secao="config"
          />
        </div>

        {/* Resumo — o botão de ação abre o modal de pagamento */}
        <CheckoutSummary
          vehicleCount={props.vehicleCount}
          currentVehicleLimit={props.currentVehicleLimit}
          basePrice={props.basePrice}
          totalPrice={props.totalPrice}
          discount={props.discount}
          finalPrice={props.finalPrice}
          onCheckout={() => setModalAberto(true)}
          paymentMethod={paymentMethod}
          selectedPlan={props.selectedPlan}
          couponCode={props.couponCode}
          setCouponCode={props.setCouponCode}
          onCouponApply={props.onCouponApply}
          isButtonsDisabled={isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice}
          countdown={props.countdown}
          temPlanoAtivo={props.temPlanoAtivo}
          diasRestantes={props.diasRestantes}
          precoAtual={props.precoAtual}
          precoNovo={props.precoNovo}
          precoMensalSemDesconto={props.precoMensalSemDesconto}
          hasActivePayment={hasActivePayment}
          tipoPlano={props.tipoPlano}
          acao={props.acao}
          currentPlanType={props.currentPlanType}
          incluirLocaSmartCheck={props.incluirLocaSmartCheck}
          precoLocaSmartCheck={props.precoLocaSmartCheckCiclo}
          onRemoveLocaSmartCheck={() => props.setIncluirLocaSmartCheck(false)}
          descontoPercentual={descontoAjustado}
          indicadorNome={props.indicadorNome}
        />
      </div>

      {/* Modal de pagamento (cartão ou PIX) */}
      {modalAberto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={fecharModal}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-[#1F467E] flex items-center gap-2">
                {paymentMethod === 'pix' ? <><QrCode size={18} /> Pagamento via PIX</> : <><CreditCard size={18} /> Dados do cartão</>}
              </h3>
              <button
                onClick={fecharModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
                aria-label="Fechar"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-5">
              <CheckoutForm
                vehicleCount={props.vehicleCount}
                setVehicleCount={props.setVehicleCount}
                paymentMethod={paymentMethod}
                selectedPlan={props.selectedPlan}
                setSelectedPlan={props.setSelectedPlan}
                pixData={paymentMethod === 'pix' ? props.pixData : undefined}
                isSubmitting={isSubmitting || hasActivePayment}
                onCheckout={onCheckout}
                formErrors={props.formErrors}
                hasActivePayment={hasActivePayment}
                hasPendingInvoice={hasPendingInvoice}
                isExpansion={false}
                isUpgrade={false}
                minVehicleLimit={props.minVehicleLimit}
                incluirLocaSmartCheck={props.incluirLocaSmartCheck}
                setIncluirLocaSmartCheck={props.setIncluirLocaSmartCheck}
                isLocaSmartCheckAtivo={props.isLocaSmartCheckAtivo}
                precoLocaSmartCheckMensal={props.precoLocaSmartCheckMensal}
                planDiscount={props.planDiscount}
                secao="pagamento"
              />
            </div>

            <div className="px-5 pb-5 pt-1">
              {hasActivePayment ? (
                <div className="w-full py-4 bg-gray-100 text-gray-500 rounded-xl text-center font-bold">
                  Processando pagamento...
                </div>
              ) : !(paymentMethod === 'pix' && props.pixData) ? (
                <button
                  onClick={onCheckout}
                  disabled={acoesBloqueadas || props.finalPrice <= 0}
                  className={`w-full py-4 rounded-xl text-white text-lg font-extrabold flex justify-center items-center gap-2 transition-all shadow-md ${
                    acoesBloqueadas || props.finalPrice <= 0
                      ? 'bg-gray-400 cursor-not-allowed opacity-60'
                      : 'bg-[#1F467E] hover:bg-[#162f56] active:scale-95'
                  }`}
                >
                  {paymentMethod === 'pix' ? (<>Gerar QR Code PIX <QrCode size={20} /></>) : (<>Pagar {fmt(props.finalPrice)} <CreditCard size={20} /></>)}
                </button>
              ) : null}
              <div className="flex items-center justify-center mt-3 text-gray-400 text-xs gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Ambiente Seguro e Criptografado
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepPayment;
